import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { HexColorPicker, RgbaStringColorPicker, HexColorInput } from "react-colorful";
import rgbHex from "rgb-hex";
import { CopyToClipboard } from "react-copy-to-clipboard";
import Dropdown from "./Dropdown";
import useDebounce from "./UseDebounce";
import { addColorToPallete } from "../../actions/UserActions";

const expandHex = (input) => {
	let hex = String(input).replace("#", "");
	if (hex.length === 3 || hex.length === 4) {
		hex = hex
			.split("")
			.map((ch) => ch + ch)
			.join("");
	}
	return `#${hex}`;
};
const hexToRgba = (input) => {
	const hex = expandHex(input).replace("#", "");

	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	const rawAlpha = parseInt(hex.substring(6, 8), 16);
	const a = Number.isNaN(rawAlpha) ? 1 : Math.round((rawAlpha / 255) * 100) / 100;

	return `rgba(${r}, ${g}, ${b}, ${a})`;
};
const toHex = (color, type) => {
	if (!color) return color;
	if (type === "hex") return color;
	try {
		return rgbHex(color);
	} catch {
		return "";
	}
};
const expandForType = (typed, type) => type === "rgba" ? hexToRgba(typed) : expandHex(typed);

function ColorPallete({ item, value, type, updateAction, dropdown, isLeft, extra }) {
	const dispatch = useDispatch();
	const { currentCampaignId, colorPallete } = useSelector((state) => state.user);

	// the canonical value that drives the picker and is sent upstream.
	const [color, setColor] = useState(value || "");
	// exactly what's shown in the hex field
	const [hexText, setHexText] = useState(toHex(value || "", type) || "");
	const debouncedColor = useDebounce(color, 600);

	// The canonical colour last produced by the hex input. While `color`
	// equals this, the change came from typing and we leave the text alone;
	// any other source (picker, recent swatch, parent) re-syncs the field.
	const lastTypedColor = useRef(null);

	const recentColors = colorPallete?.[currentCampaignId]?.[type] || [];
	const fullHex = toHex(color, type);

	// Commit the debounced colour upstream and remember it in the palette.
	useEffect(() => {
		if (!updateAction || !color || color === value) return;

		const next = debouncedColor ? debouncedColor.toLowerCase() : debouncedColor;
		updateAction(item, next, extra);

		if (next && !recentColors.includes(next)) {
			dispatch(addColorToPallete(next, type, currentCampaignId));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedColor]);

	useEffect(() => {
		setColor(value || "");
	}, [value]);

	// Keep the hex field in step with the canonical colour but not while the user is typing into it, otherwise their shorthand gets expanded.
	useEffect(() => {
		if (color === lastTypedColor.current) return;
		lastTypedColor.current = null;
		setHexText(toHex(color, type) || "");
	}, [color, type]);

	const handleHexInput = (typed) => {
		const expanded = expandForType(typed, type);
		lastTypedColor.current = expanded;
		setHexText(typed);
		setColor(expanded);
	};

	const reset = () => updateAction?.(item, null, extra);

	return (
		<div className="colorpicker__wrap">
			{color && (
				<span
					className="material-icons colorpicker__reset pointer"
					role="button"
					tabIndex={0}
					onClick={reset}
					onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && reset()}
				>
					clear
				</span>
			)}
			<Dropdown dropup={!dropdown}>
				<div className="dropdown-trigger colorpicker">
					<div
						className="colorpicker__color colorpicker__color-lg text--center"
						style={{ background: color }}
					>
						{!color && <span className="material-icons font--20 mtop--5">invert_colors</span>}
					</div>
				</div>
				<div
					className="dropdown__menu"
					style={{ minWidth: "236px", marginLeft: isLeft ? "-236px" : undefined }}
				>
					<div className="dropdown__content colorpicker__dropdown">
						<div className="colorpicker__picker">
							{type === "hex" && <HexColorPicker color={color || "#b6c0c9"} onChange={setColor} />}
							{type === "rgba" && (
								<RgbaStringColorPicker
									color={color || "rgba(182, 182, 201, 1)"}
									onChange={setColor}
								/>
							)}
							<div className="react-colorful__input text--center">
								<span className="vm--">#&nbsp;</span>
								<span className="vm--align">
									<HexColorInput
										onClick={(e) => e.stopPropagation()}
										color={hexText}
										onChange={handleHexInput}
										alpha={type === "rgba"}
									/>
								</span>
								<CopyToClipboard text={fullHex}>
									<button className="btn btn-secondary btn-sm">
										<span className="material-icons font--22">content_copy</span>
									</button>
								</CopyToClipboard>
							</div>
							<div className="mtop--20 colorpicker__picker-recent">
								<label className="text--bold font--11">Recently used ({type})</label>
								<div className="colorpicker flex flex--row flex--wrap">
									{recentColors.map((c, i) => (
										<div
											key={`${c}__${i}`}
											role="button"
											tabIndex={0}
											onClick={() => setColor(c)}
											onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setColor(c)}
											className="colorpicker__color text--center mleft--5 mtop--5"
											style={{ background: c }}
										/>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</Dropdown>
		</div>
	);
}

export default ColorPallete;
